import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { toast } from "sonner";
import {
  Phone,
  MapPin,
  Clock,
  Mail,
  MessageCircle,
  ArrowUpRight,
  Sparkles,
  Stethoscope,
  Smile,
  Baby,
  Wand2,
  Anchor,
  Activity,
  Star,
  Quote,
  Crown,
  Scissors,
  Brush,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLINIC_NAME = "Mahajan Dental Care Clinic";
const WHATSAPP = "https://wa.me/918699820619?text=Hi%20Mahajan%20Dental%2C%20I%27d%20like%20to%20book%20an%20appointment.";
const PHONE = "+91 86998 20619";
const PHONE_TEL = "tel:+918699820619";
const EMAIL = "sidharthmahajan.ss@gmail.com";
const ADDRESS = "Library Road, Hanuman Chowk, Gurdaspur";
const MAPS_URL = "https://maps.google.com/maps?vet=10CAAQoqAOahcKEwiYnPKKkraUAxUAAAAAHQAAAAAQCQ..i&rlz=1C5CHFA_enIN1150IN1150&pvq=CgwvZy8xaGR6bTlnOXEiGwoVbWFoYWphbiBkZW50YWwgY2xpbmljEAIYAw&lqi=ChVtYWhhamFuIGRlbnRhbCBjbGluaWNItvLS6bOPgIAIWiEQABABEAIYABgBIhVtYWhhamFuIGRlbnRhbCBjbGluaWOSAQ1kZW50YWxfY2xpbmlj&fvr=1&cs=1&um=1&ie=UTF-8&fb=1&gl=in&sa=X&ftid=0x391b92719cac2f95:0xd3fcac47836d4ab5";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/ef2cd6a1-34e3-41ce-a47e-7c3d30254c58/images/ea5fddffb4a057c095b864c1a94645ec832e0772d2ae746d02bc56235ade9afa.png";
const ABOUT_IMG =
  "https://customer-assets.emergentagent.com/job_smilecare-dental-12/artifacts/bz2dyupo_WhatsApp%20Image%202026-05-15%20at%2014.44.23.jpeg";
const CONTACT_IMG =
  "https://images.unsplash.com/photo-1704455306925-1401c3012117?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjYWxtJTIwZGVudGFsJTIwY2xpbmljJTIwaW50ZXJpb3IlMjB3YXJtJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzc4NjcwNTIwfDA&ixlib=rb-4.1.0&q=85";
const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_smilecare-dental-12/artifacts/1w32ge5m_WhatsApp%20Image%202026-05-15%20at%2014.44.24.jpeg";
const BROCHURE_IMG =
  "https://customer-assets.emergentagent.com/job_smilecare-dental-12/artifacts/pj5ndbca_WhatsApp%20Image%202026-05-15%20at%2014.52.39.jpeg";
const CASE_STUDY_IMG =
  "https://customer-assets.emergentagent.com/job_smilecare-dental-12/artifacts/7k6h4n1t_new.jpeg";
const EVENT_VIDEO =
  "https://customer-assets-0z36b82j.emergentagent.net/job_smilecare-dental-12/artifacts/xl20ezik_12.mp4";
const CLINIC_VIDEOS = [
  "https://customer-assets.emergentagent.com/job_smilecare-dental-12/artifacts/ap3if9q5_WhatsApp%20Video%202026-05-15%20at%2014.44.24.mp4",
  "https://customer-assets.emergentagent.com/job_smilecare-dental-12/artifacts/97kveq9l_WhatsApp%20Video%202026-05-15%20at%2014.44.25.mp4",
  "https://customer-assets.emergentagent.com/job_smilecare-dental-12/artifacts/4wp691b7_WhatsApp%20Video%202026-05-15%20at%2014.52.11.mp4",
];

const services = [
  { title: "Orthodontic Treatment", desc: "Braces and clear aligners planned by our in-house orthodontists.", icon: Smile, accent: "primary" },
  { title: "Implant Center", desc: "Full-arch and single-tooth dental implants — surgically precise, biologically gentle.", icon: Anchor, accent: "secondary" },
  { title: "Tooth Capping", desc: "Tooth-coloured crowns and caps that restore strength and aesthetics.", icon: Crown, accent: "primary" },
  { title: "Root Canal Therapy", desc: "Modern, near-painless endodontics with single-visit options.", icon: Activity, accent: "secondary" },
  { title: "Cleaning & Scaling", desc: "Ultrasonic cleaning and polishing for healthier gums and brighter teeth.", icon: Brush, accent: "primary" },
  { title: "Surgical & Non-Surgical Extractions", desc: "Wisdom teeth and routine extractions, performed with care and discretion.", icon: Scissors, accent: "secondary" },
  { title: "Smile Development", desc: "Cosmetic smile design tailored to the geometry of your face.", icon: Wand2, accent: "primary" },
  { title: "Pediatric Consultation", desc: "Patient, story-led dental visits for children and teenagers.", icon: Baby, accent: "secondary" },
];

const testimonials = [
  { name: "Aanya K.", text: "Calmest dental clinic I've visited. My implants healed beautifully and the team explained every step.", rating: 5 },
  { name: "Rohan S.", text: "Painless root canal — I genuinely could not believe it. The interiors feel more spa than clinic.", rating: 5 },
  { name: "Meera P.", text: "Got Invisalign here. Six months in and my bite already feels different. Highly recommended.", rating: 5 },
  { name: "Harpreet G.", text: "Brought my 7-year-old. They turned a scary first visit into a story he keeps retelling.", rating: 5 },
  { name: "Devika R.", text: "Whitening result was subtle and natural — exactly what I wanted. No sensitivity at all.", rating: 5 },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

function Nav() {
  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 backdrop-blur-xl bg-[#F9F8F6]/75 border-b border-[#2D332F]/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 h-16 flex items-center justify-between">
        <a href="#top" data-testid="logo-link" className="flex items-center gap-2.5 group">
          <img src={LOGO_URL} alt="Mahajan Dental Clinic logo" className="h-10 w-10 rounded-full object-contain bg-white border border-[#2D332F]/10 p-0.5" />
          <span className="font-serif-display text-2xl text-[#2D332F]">Mahajan Dental</span>
        </a>
        <nav className="hidden md:flex items-center gap-10 text-sm text-[#5C6660]">
          <a href="#about" data-testid="nav-about" className="hover:text-[#2D332F] transition">About</a>
          <a href="#services" data-testid="nav-services" className="hover:text-[#2D332F] transition">Services</a>
          <a href="#clinic" data-testid="nav-clinic" className="hover:text-[#2D332F] transition">Clinic</a>
          <a href="#testimonials" data-testid="nav-testimonials" className="hover:text-[#2D332F] transition">Stories</a>
          <a href="#contact" data-testid="nav-contact" className="hover:text-[#2D332F] transition">Visit</a>
        </nav>
        <a
          href={WHATSAPP}
          target="_blank"
          rel="noreferrer"
          data-testid="header-book-btn"
          className="inline-flex items-center gap-2 rounded-full bg-[#2D332F] text-[#F9F8F6] px-5 py-2.5 text-sm hover:bg-[#1A201C] transition"
        >
          Book Appointment
          <ArrowUpRight className="h-4 w-4" strokeWidth={1.6} />
        </a>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden grain"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-20 md:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="lg:col-span-5"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-[#2D332F]/10 bg-white/60 px-3 py-1 text-xs tracking-wide text-[#5C6660]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#6F8877]" />
              Now accepting new patients — Mahajan Dental Care
            </div>

            <h1 className="mt-6 font-serif-display text-[#2D332F] text-5xl md:text-6xl lg:text-7xl leading-[1.02]">
              Healthy smiles,<br />
              <span className="italic text-[#6F8877]">quietly crafted.</span>
            </h1>

            <p className="mt-6 text-[#5C6660] text-lg max-w-md leading-relaxed">
              A sanctuary-style dental practice by Dr. Mahajan — pairing modern
              technology with the calm pace of considered, careful care.
            </p>

            <div className="mt-9 flex flex-wrap gap-3">
              <a
                href={WHATSAPP}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-book-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[#6F8877] hover:bg-[#5a6e60] text-white px-6 py-3.5 text-sm transition shadow-sm"
              >
                <MessageCircle className="h-4 w-4" strokeWidth={1.6} />
                Book on WhatsApp
              </a>
              <a
                href={PHONE_TEL}
                data-testid="hero-call-btn"
                className="inline-flex items-center gap-2 rounded-full border border-[#2D332F]/15 bg-white text-[#2D332F] px-6 py-3.5 text-sm hover:bg-[#F2EFE9] transition"
              >
                <Phone className="h-4 w-4" strokeWidth={1.6} />
                Call clinic
              </a>
            </div>

            <div className="mt-10 grid grid-cols-2 gap-6 max-w-md">
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 mt-1 text-[#6F8877]" strokeWidth={1.5} />
                <div>
                  <p className="text-xs uppercase tracking-widest text-[#5C6660]">Location</p>
                  <a href={MAPS_URL} target="_blank" rel="noreferrer" className="text-sm text-[#2D332F] hover:text-[#6F8877] transition">View on map</a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="h-4 w-4 mt-1 text-[#6F8877]" strokeWidth={1.5} />
                <div>
                  <p className="text-xs uppercase tracking-widest text-[#5C6660]">Hours</p>
                  <p className="text-sm text-[#2D332F]">Mon–Sat · 9AM–7PM</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7 relative"
          >
            <div className="relative rounded-[2rem] overflow-hidden border border-[#2D332F]/10 shadow-[0_30px_80px_-40px_rgba(45,51,47,0.35)]">
              <img
                src={HERO_IMG}
                alt="Sculpted ceramic tooth on a sandstone pedestal"
                data-testid="hero-image"
                className="w-full h-[460px] md:h-[560px] object-cover"
              />
              <div className="absolute bottom-5 left-5 right-5 md:bottom-7 md:left-7 md:right-auto md:max-w-xs rounded-2xl bg-white/85 backdrop-blur px-5 py-4 border border-white/60">
                <div className="flex items-center gap-2 text-[#2D332F]">
                  <Stethoscope className="h-4 w-4 text-[#D48C70]" strokeWidth={1.5} />
                  <span className="text-xs uppercase tracking-widest">Today's promise</span>
                </div>
                <p className="font-serif-display text-xl mt-1 leading-snug">
                  No rushed chairs. Just unhurried, careful dentistry.
                </p>
              </div>
            </div>

            <div className="absolute -top-6 -right-2 md:-right-6 hidden md:flex items-center gap-2 rounded-full bg-[#D48C70] text-white px-4 py-2 text-xs tracking-wide shadow-lg rotate-3">
              <Sparkles className="h-3.5 w-3.5" strokeWidth={1.8} />
              ISO-certified sterilisation
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function StatsRibbon() {
  const stats = [
    { k: "12+", v: "years in practice" },
    { k: "8,400", v: "smiles cared for" },
    { k: "4.9★", v: "Google rating" },
    { k: "0", v: "rushed appointments" },
  ];
  return (
    <section data-testid="stats-ribbon" className="border-y border-[#2D332F]/10 bg-[#F2EFE9]/60">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
        {stats.map((s) => (
          <div key={s.v} className="flex items-baseline gap-3">
            <span className="font-serif-display text-4xl md:text-5xl text-[#2D332F]">{s.k}</span>
            <span className="text-sm text-[#5C6660] tracking-wide">{s.v}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function About() {
  return (
    <section id="about" data-testid="about-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div className="relative">
          <div className="rounded-[2rem] overflow-hidden border border-[#2D332F]/10">
            <img
              src={ABOUT_IMG}
              alt="Mahajan Dental Care Clinic storefront and signage"
              className="w-full h-[480px] object-cover"
            />
          </div>
          <div className="hidden md:block absolute -bottom-8 -right-6 bg-white border border-[#2D332F]/10 rounded-2xl p-5 max-w-xs shadow-sm">
            <div className="flex items-center gap-2 mb-2 text-[#6F8877]">
              <Quote className="h-4 w-4" strokeWidth={1.5} />
              <span className="text-xs uppercase tracking-widest text-[#5C6660]">Our philosophy</span>
            </div>
            <p className="font-serif-display text-lg leading-snug text-[#2D332F]">
              Dentistry should feel like a long exhale — not a held breath.
            </p>
          </div>
        </div>

        <div>
          <span className="text-xs uppercase tracking-[0.25em] text-[#5C6660]">About the practice</span>
          <h2 className="mt-3 font-serif-display text-4xl md:text-5xl text-[#2D332F] leading-tight">
            A sanctuary for your<br />
            <span className="italic text-[#6F8877]">smile</span>, crafted by Dr. Mahajan.
          </h2>
          <p className="mt-6 text-[#5C6660] text-lg leading-relaxed">
            Mahajan Dental Care Clinic blends clinical precision with the cadence of a slow morning.
            Every operatory is designed around natural light, every plan around the
            person in the chair. We treat fewer patients each day — on purpose —
            so we can treat each one fully.
          </p>

          <ul className="mt-8 space-y-3">
            {[
              "Digital impressions — no goopy moulds.",
              "Single-visit crowns with in-house CAD/CAM.",
              "Sedation options for anxious patients.",
              "Transparent, written treatment estimates.",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3 text-[#2D332F]">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#D48C70]" />
                <span className="text-base">{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function Services() {
  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-32 bg-[#F2EFE9]/50 border-y border-[#2D332F]/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <span className="text-xs uppercase tracking-[0.25em] text-[#5C6660]">What we do</span>
            <h2 className="mt-3 font-serif-display text-4xl md:text-5xl text-[#2D332F] leading-tight">
              Eight quiet specialisms.<br />
              <span className="italic text-[#6F8877]">One unhurried clinic.</span>
            </h2>
          </div>
          <p className="text-[#5C6660] max-w-sm">
            From preventive cleanings to full smile design — every treatment is
            consultative, never transactional.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s, i) => {
            const Icon = s.icon;
            const accent = s.accent === "primary" ? "#6F8877" : "#D48C70";
            return (
              <motion.article
                key={s.title}
                data-testid={`service-card-${i}`}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                custom={i}
                variants={fadeUp}
                className="group bg-white rounded-2xl border border-[#2D332F]/10 p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_-30px_rgba(45,51,47,0.35)]"
              >
                <div
                  className="h-11 w-11 rounded-xl grid place-items-center mb-6"
                  style={{ background: `${accent}14` }}
                >
                  <Icon className="h-5 w-5" strokeWidth={1.5} style={{ color: accent }} />
                </div>
                <h3 className="font-serif-display text-2xl text-[#2D332F]">{s.title}</h3>
                <p className="mt-3 text-[#5C6660] text-sm leading-relaxed">{s.desc}</p>
                <div className="mt-6 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#5C6660] group-hover:text-[#2D332F] transition">
                  Learn more <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.6} />
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ClinicGallery() {
  return (
    <section id="clinic" data-testid="clinic-gallery-section" className="py-24 md:py-32 bg-[#F2EFE9]/30 border-t border-[#2D332F]/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <span className="text-xs uppercase tracking-[0.25em] text-[#5C6660]">Inside the clinic</span>
            <h2 className="mt-3 font-serif-display text-4xl md:text-5xl text-[#2D332F] leading-tight">
              A quiet look<br />
              <span className="italic text-[#6F8877]">around the practice.</span>
            </h2>
          </div>
          <p className="text-[#5C6660] max-w-sm">
            A look at the clinic in motion — and our printed services brochure
            in English &amp; Punjabi for visitors at Library Road, Hanuman Chowk, Gurdaspur.
          </p>
        </div>

        <div className="space-y-5">
          <div
            data-testid="latest-event-video"
            className="relative rounded-[1.5rem] overflow-hidden border border-[#2D332F]/10 bg-[#1A201C]"
          >
            <video
              src={EVENT_VIDEO}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              controls
              className="w-full h-[420px] md:h-[560px] object-cover"
            />
            <div className="absolute top-4 left-4 flex items-center gap-2 rounded-full bg-[#D48C70] text-white px-3 py-1.5 text-[11px] tracking-[0.2em] uppercase shadow-lg">
              <Sparkles className="h-3.5 w-3.5" strokeWidth={1.8} />
              Latest event
            </div>
            <div className="absolute bottom-4 left-4 right-4 md:bottom-6 md:left-6 md:right-auto md:max-w-md rounded-2xl bg-white/85 backdrop-blur px-5 py-4 border border-white/60">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#5C6660]">A recent moment at the clinic</p>
              <p className="mt-1 font-serif-display text-xl text-[#2D332F] leading-tight">
                Highlights from our latest patient event.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            <a
              href={BROCHURE_IMG}
              target="_blank"
              rel="noreferrer"
              data-testid="clinic-brochure"
              className="lg:col-span-7 group block relative rounded-[1.5rem] overflow-hidden border border-[#2D332F]/10 bg-white aspect-[4/3] lg:aspect-auto lg:min-h-[440px]"
            >
              <img
                src={BROCHURE_IMG}
                alt="Mahajan Dental Care Clinic services brochure — Library Road, Hanuman Chowk, Gurdaspur"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
              />
              <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3">
                <div className="rounded-xl bg-white/90 backdrop-blur px-4 py-2.5 border border-white/60">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#5C6660]">Services brochure</p>
                  <p className="font-serif-display text-base text-[#2D332F] leading-tight">English &amp; Punjabi — for our local patients</p>
                </div>
                <span className="rounded-full bg-[#2D332F] text-white p-2.5 opacity-90 group-hover:opacity-100">
                  <ArrowUpRight className="h-4 w-4" strokeWidth={1.6} />
                </span>
              </div>
            </a>

            <a
              href={CASE_STUDY_IMG}
              target="_blank"
              rel="noreferrer"
              data-testid="clinic-case-study"
              className="lg:col-span-5 group block relative rounded-[1.5rem] overflow-hidden border border-[#2D332F]/10 bg-white aspect-square lg:aspect-auto lg:min-h-[440px]"
            >
              <img
                src={CASE_STUDY_IMG}
                alt="Transforming Smiles — restorative dentistry journey from initial consultation to the finished smile"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
              />
              <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3">
                <div className="rounded-xl bg-white/90 backdrop-blur px-4 py-2.5 border border-white/60">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#5C6660]">Case study</p>
                  <p className="font-serif-display text-base text-[#2D332F] leading-tight">Restorative dentistry — before &amp; after</p>
                </div>
                <span className="rounded-full bg-[#2D332F] text-white p-2.5 opacity-90 group-hover:opacity-100">
                  <ArrowUpRight className="h-4 w-4" strokeWidth={1.6} />
                </span>
              </div>
            </a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {CLINIC_VIDEOS.map((src, i) => (
              <div
                key={src}
                data-testid={`clinic-video-${i}`}
                className="relative rounded-[1.5rem] overflow-hidden border border-[#2D332F]/10 bg-[#1A201C] aspect-video"
              >
                <video
                  src={src}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  controls
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section id="testimonials" data-testid="testimonials-section" className="py-24 md:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 mb-12">
        <span className="text-xs uppercase tracking-[0.25em] text-[#5C6660]">Patient stories</span>
        <h2 className="mt-3 font-serif-display text-4xl md:text-5xl text-[#2D332F] leading-tight max-w-3xl">
          Quiet praise from the people who matter — <span className="italic text-[#6F8877]">our patients.</span>
        </h2>
      </div>

      <Marquee gradient gradientColor="#F9F8F6" gradientWidth={120} speed={32} pauseOnHover>
        {testimonials.concat(testimonials).map((t, i) => (
          <article
            key={`${t.name}-${i}`}
            data-testid={`testimonial-${i}`}
            className="mx-3 w-[340px] md:w-[420px] shrink-0 bg-white rounded-2xl border border-[#2D332F]/10 p-7"
          >
            <div className="flex items-center gap-1 mb-4 text-[#D48C70]">
              {Array.from({ length: t.rating }).map((_, idx) => (
                <Star key={idx} className="h-4 w-4 fill-[#D48C70] stroke-[#D48C70]" />
              ))}
            </div>
            <p className="text-[#2D332F] leading-relaxed">
              &ldquo;{t.text}&rdquo;
            </p>
            <p className="mt-5 text-sm text-[#5C6660] tracking-wide">— {t.name}, patient</p>
          </article>
        ))}
      </Marquee>
    </section>
  );
}

function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Name and phone are required.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Thank you — we'll call you back within a few hours.");
      setForm({ name: "", phone: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Could not send. Please try WhatsApp or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-[#F2EFE9]/60 border-t border-[#2D332F]/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-stretch">
        <div className="lg:col-span-5 relative rounded-[2rem] overflow-hidden border border-[#2D332F]/10 min-h-[620px]">
          <img src={CONTACT_IMG} alt="Calm modern dental clinic interior" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A201C]/95 via-[#1A201C]/75 to-[#1A201C]/40" />
          <div className="relative h-full p-8 md:p-10 flex flex-col justify-end text-[#F9F8F6]">
            <span className="text-xs uppercase tracking-[0.25em] text-[#F9F8F6]/70">Visit us</span>
            <h3 className="mt-3 font-serif-display text-4xl md:text-5xl leading-tight">
              Step into a calmer<br />kind of dentistry.
            </h3>

            <div className="mt-8" data-testid="doctors-block">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#F9F8F6]/60">Our doctors</p>
              <ul className="mt-3 space-y-2.5 text-sm text-[#F9F8F6]/90">
                <li>
                  <p className="font-serif-display text-lg leading-tight text-white">Dr. Sidharth Mahajan</p>
                  <p className="text-[11px] tracking-wide text-[#F9F8F6]/70">B.D.S, M.D.S — Orthodontics & Dentofacial Orthopedics · Braces Specialist</p>
                </li>
                <li>
                  <p className="font-serif-display text-lg leading-tight text-white">Dr. Vaishanavi Mahajan</p>
                  <p className="text-[11px] tracking-wide text-[#F9F8F6]/70">B.D.S, M.D.S — Orthodontics & Dentofacial Orthopedics · Braces Specialist</p>
                </li>
                <li>
                  <p className="font-serif-display text-lg leading-tight text-white">Dr. Sunil Mahajan</p>
                  <p className="text-[11px] tracking-wide text-[#F9F8F6]/70">B.D.S · Medalist</p>
                </li>
              </ul>
            </div>

            <div className="mt-7 pt-6 border-t border-white/15 space-y-3 text-[#F9F8F6]/90 text-sm">
              <a href={PHONE_TEL} data-testid="contact-phone" className="flex items-center gap-3 hover:text-white transition">
                <Phone className="h-4 w-4" strokeWidth={1.5} /> {PHONE}
              </a>
              <a href={`mailto:${EMAIL}`} data-testid="contact-email" className="flex items-center gap-3 hover:text-white transition">
                <Mail className="h-4 w-4" strokeWidth={1.5} /> {EMAIL}
              </a>
              <div className="flex items-center gap-3">
                <MapPin className="h-4 w-4" strokeWidth={1.5} />
                <a href={MAPS_URL} target="_blank" rel="noreferrer" data-testid="contact-map-link" className="hover:text-white transition">
                  {ADDRESS} · View on map
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="h-4 w-4" strokeWidth={1.5} /> Mon–Sat · 9AM–7PM
              </div>
            </div>
            <a
              href={WHATSAPP}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp-btn"
              className="mt-8 inline-flex items-center gap-2 self-start rounded-full bg-white text-[#1A201C] px-5 py-3 text-sm hover:bg-[#F2EFE9] transition"
            >
              <MessageCircle className="h-4 w-4" strokeWidth={1.6} />
              Chat on WhatsApp
            </a>
          </div>
        </div>

        <div className="lg:col-span-7 bg-white rounded-[2rem] border border-[#2D332F]/10 p-8 md:p-12">
          <span className="text-xs uppercase tracking-[0.25em] text-[#5C6660]">Send an inquiry</span>
          <h3 className="mt-3 font-serif-display text-3xl md:text-4xl text-[#2D332F] leading-tight">
            Book your first consultation.
          </h3>
          <p className="mt-3 text-[#5C6660]">
            Share a few details and we'll call you back to schedule a quiet time
            that suits you.
          </p>

          <form onSubmit={onSubmit} data-testid="inquiry-form" className="mt-8 space-y-5">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#5C6660]" htmlFor="name">Your name</label>
              <Input
                id="name"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="e.g. Aanya Kapoor"
                data-testid="inquiry-name-input"
                className="mt-2 h-12 rounded-xl border-[#2D332F]/15 focus-visible:ring-[#6F8877]"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#5C6660]" htmlFor="phone">Phone</label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={form.phone}
                onChange={handleChange}
                placeholder="+91 ..."
                data-testid="inquiry-phone-input"
                className="mt-2 h-12 rounded-xl border-[#2D332F]/15 focus-visible:ring-[#6F8877]"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#5C6660]" htmlFor="message">What brings you in?</label>
              <Textarea
                id="message"
                name="message"
                value={form.message}
                onChange={handleChange}
                rows={4}
                placeholder="Routine check-up, implant consultation, smile makeover..."
                data-testid="inquiry-message-input"
                className="mt-2 rounded-xl border-[#2D332F]/15 focus-visible:ring-[#6F8877]"
              />
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Button
                type="submit"
                disabled={loading}
                data-testid="inquiry-submit-btn"
                className="rounded-full bg-[#6F8877] hover:bg-[#5a6e60] text-white h-12 px-7"
              >
                {loading ? "Sending..." : "Send inquiry"}
                <ArrowUpRight className="h-4 w-4 ml-1" strokeWidth={1.6} />
              </Button>
              <a
                href={WHATSAPP}
                target="_blank"
                rel="noreferrer"
                data-testid="form-whatsapp-btn"
                className="inline-flex items-center gap-2 rounded-full border border-[#2D332F]/15 text-[#2D332F] h-12 px-6 text-sm hover:bg-[#F2EFE9] transition"
              >
                <MessageCircle className="h-4 w-4" strokeWidth={1.6} />
                Prefer WhatsApp
              </a>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-[#1A201C] text-[#F9F8F6] pt-24 pb-10 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 pb-16 border-b border-white/10">
          <div>
            <div className="flex items-center gap-2.5">
              <img src={LOGO_URL} alt="Mahajan Dental Clinic logo" className="h-10 w-10 rounded-full object-contain bg-white p-0.5" />
              <span className="font-serif-display text-2xl">Mahajan Dental</span>
            </div>
            <p className="mt-5 text-[#F9F8F6]/70 text-sm leading-relaxed max-w-xs">
              Editorial-grade dental care by Dr. Mahajan. Quietly modern, deeply human.
            </p>
          </div>
          <div className="text-sm">
            <p className="uppercase tracking-[0.25em] text-[#F9F8F6]/50 text-xs">Visit</p>
            <a href={MAPS_URL} target="_blank" rel="noreferrer" data-testid="footer-map-link" className="block mt-4 text-[#F9F8F6]/85 hover:text-white">{ADDRESS}</a>
            <p className="text-[#F9F8F6]/85">Mon–Sat · 9AM–7PM</p>
          </div>
          <div className="text-sm">
            <p className="uppercase tracking-[0.25em] text-[#F9F8F6]/50 text-xs">Connect</p>
            <a href={PHONE_TEL} className="block mt-4 text-[#F9F8F6]/85 hover:text-white">{PHONE}</a>
            <a href={`mailto:${EMAIL}`} className="block text-[#F9F8F6]/85 hover:text-white">{EMAIL}</a>
            <a href={WHATSAPP} target="_blank" rel="noreferrer" className="block text-[#F9F8F6]/85 hover:text-white">WhatsApp</a>
          </div>
        </div>

        <div className="pt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-[#F9F8F6]/50">
          <p>© {new Date().getFullYear()} {CLINIC_NAME}. All rights reserved.</p>
          <p>Crafted with care</p>
        </div>

        <div
          aria-hidden
          className="font-serif-display select-none text-[#F9F8F6]/[0.06] leading-none mt-6"
          style={{ fontSize: "clamp(60px, 14vw, 220px)" }}
        >
          Mahajan Dental.
        </div>
      </div>
    </footer>
  );
}

export default function Landing() {
  return (
    <div className="bg-[#F9F8F6] text-[#2D332F]" data-testid="landing-root">
      <Nav />
      <Hero />
      <StatsRibbon />
      <About />
      <Services />
      <ClinicGallery />
      <Testimonials />
      <Contact />
      <Footer />
    </div>
  );
}
